import React, { useState, useRef } from 'react';
import { Upload, FileText, Shield, CheckCircle, XCircle, Loader2, Hash } from 'lucide-react';

const SecureFileUpload = ({ 
  uploadEndpoint = 'http://88.198.60.90:8008/upload',
  maxFileSize = 100, // in MB
  onUploadSuccess = () => {},
  onUploadError = () => {},
  className = '',
  showSecurityFeatures = true
}) => {
  const [file, setFile] = useState(null);
  const [fileHash, setFileHash] = useState('');
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, hashing, uploading, scanning, confirmed, rejected
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hashProgress, setHashProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Calculate SHA-256 hash of file with progress tracking
  const calculateFileHash = async (file) => {
    const chunkSize = 1024 * 1024; // 1MB chunks
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    let buffer = new Uint8Array(0);

    // Read file in chunks for better performance with large files
    const readChunk = (start, end) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(start, end));
      });
    };

    // Process file in chunks
    const chunks_array = [];
    while (currentChunk < chunks) {
      const start = currentChunk * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = await readChunk(start, end);
      chunks_array.push(chunk);
      
      currentChunk++;
      setHashProgress(Math.round((currentChunk / chunks) * 100));
    }

    // Combine all chunks
    const totalLength = chunks_array.reduce((acc, chunk) => acc + chunk.length, 0);
    buffer = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks_array) {
      buffer.set(chunk, offset);
      offset += chunk.length;
    }

    // Calculate hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size
      if (selectedFile.size > maxFileSize * 1024 * 1024) {
        setError(`File size exceeds ${maxFileSize}MB limit`);
        return;
      }
      setFile(selectedFile);
      setFileHash('');
      setError('');
      setStatusMessage('');
      setUploadStatus('idle');
      setHashProgress(0);
      setUploadProgress(0);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setError('');
    setUploadStatus('hashing');
    setStatusMessage('Calculating file hash for integrity verification...');

    try {
      // Calculate file hash first
      const hash = await calculateFileHash(file);
      setFileHash(hash);
      setStatusMessage(`File hash: ${hash.substring(0, 16)}...`);
      
      // Brief pause to show the hash
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Start upload
      setUploadStatus('uploading');
      setStatusMessage('Uploading file with hash verification...');
      setHashProgress(0);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_hash', hash);

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Create promise to handle xhr
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status === 200 || xhr.status === 406) {
            resolve({
              status: xhr.status,
              data: JSON.parse(xhr.responseText)
            });
          } else {
            reject(new Error(JSON.parse(xhr.responseText).detail || 'Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });
      });

      // Send request
      xhr.open('POST', uploadEndpoint);
      xhr.send(formData);

      // After upload completes, show scanning status
      setUploadStatus('scanning');
      setStatusMessage('File uploaded. Scanning for viruses...');

      // Wait for response
      const response = await uploadPromise;

      if (response.status === 200) {
        // File was accepted
        setUploadStatus('confirmed');
        setStatusMessage(`File verified and accepted!\nHash: ${hash.substring(0, 32)}...`);
        if (response.data.file_url) {
          setStatusMessage(prev => `${prev}\nFile URL: ${response.data.file_url}`);
        }
        onUploadSuccess({
          file,
          hash,
          response: response.data
        });
      } else if (response.status === 406) {
        // File was infected
        setUploadStatus('rejected');
        setStatusMessage(response.data.detail || 'File was rejected due to security concerns.');
        onUploadError({
          file,
          hash,
          error: response.data.detail
        });
      }
      
      setUploadProgress(0);

    } catch (err) {
      const errorMessage = err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
      setUploadStatus('idle');
      setUploadProgress(0);
      setHashProgress(0);
      onUploadError({
        file,
        error: errorMessage
      });
    }
  };

  const resetUpload = () => {
    setFile(null);
    setFileHash('');
    setUploadStatus('idle');
    setUploadProgress(0);
    setHashProgress(0);
    setStatusMessage('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'hashing':
        return <Hash className="w-12 h-12 text-purple-500 animate-pulse" />;
      case 'uploading':
        return <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />;
      case 'scanning':
        return <Shield className="w-12 h-12 text-yellow-500 animate-pulse" />;
      case 'confirmed':
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-12 h-12 text-red-500" />;
      default:
        return <Upload className="w-12 h-12 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (uploadStatus) {
      case 'hashing':
        return 'border-purple-500 bg-purple-50';
      case 'uploading':
        return 'border-blue-500 bg-blue-50';
      case 'scanning':
        return 'border-yellow-500 bg-yellow-50';
      case 'confirmed':
        return 'border-green-500 bg-green-50';
      case 'rejected':
        return 'border-red-500 bg-red-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className={`bg-white rounded-xl shadow-lg p-8 border-2 transition-all duration-300 ${getStatusColor()}`}>
        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {getStatusIcon()}
        </div>

        {/* File Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            disabled={uploadStatus !== 'idle'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {file && (
            <div className="mt-2 flex items-center text-sm text-gray-600">
              <FileText className="w-4 h-4 mr-2" />
              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </div>

        {/* Hash Progress Bar */}
        {uploadStatus === 'hashing' && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Calculating Hash</span>
              <span>{hashProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${hashProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Upload Progress Bar */}
        {uploadStatus === 'uploading' && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Upload Progress</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* File Hash Display */}
        {fileHash && uploadStatus !== 'idle' && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs font-mono text-gray-600">
              SHA-256: {fileHash.substring(0, 32)}...
            </p>
          </div>
        )}

        {/* Status Message */}
        {statusMessage && (
          <div className={`mb-6 p-4 rounded-lg ${
            uploadStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
            uploadStatus === 'rejected' ? 'bg-red-100 text-red-800' :
            uploadStatus === 'scanning' ? 'bg-yellow-100 text-yellow-800' :
            uploadStatus === 'hashing' ? 'bg-purple-100 text-purple-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            <p className="text-sm font-medium whitespace-pre-line">{statusMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          {uploadStatus === 'idle' && (
            <button
              onClick={handleUpload}
              disabled={!file}
              className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Upload File
            </button>
          )}
          
          {(uploadStatus === 'confirmed' || uploadStatus === 'rejected') && (
            <button
              onClick={resetUpload}
              className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-600 transition-colors"
            >
              Upload Another File
            </button>
          )}
        </div>

        {/* Status Steps */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-between">
            <div className={`flex flex-col items-center ${
              uploadStatus === 'hashing' || uploadStatus === 'uploading' || uploadStatus === 'scanning' || uploadStatus === 'confirmed' || uploadStatus === 'rejected'
                ? 'text-purple-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                uploadStatus !== 'idle' ? 'bg-purple-100' : 'bg-gray-100'
              }`}>
                <Hash className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium">Hash</span>
            </div>

            <div className={`flex flex-col items-center ${
              uploadStatus === 'uploading' || uploadStatus === 'scanning' || uploadStatus === 'confirmed' || uploadStatus === 'rejected'
                ? 'text-blue-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                uploadStatus === 'uploading' || uploadStatus === 'scanning' || uploadStatus === 'confirmed' || uploadStatus === 'rejected'
                  ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                1
              </div>
              <span className="text-xs font-medium">Upload</span>
            </div>
            
            <div className={`flex flex-col items-center ${
              uploadStatus === 'scanning' || uploadStatus === 'confirmed' || uploadStatus === 'rejected' 
                ? 'text-yellow-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                uploadStatus === 'scanning' || uploadStatus === 'confirmed' || uploadStatus === 'rejected'
                  ? 'bg-yellow-100' : 'bg-gray-100'
              }`}>
                2
              </div>
              <span className="text-xs font-medium">Scan</span>
            </div>
            
            <div className={`flex flex-col items-center ${
              uploadStatus === 'confirmed' ? 'text-green-600' :
              uploadStatus === 'rejected' ? 'text-red-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                uploadStatus === 'confirmed' ? 'bg-green-100' :
                uploadStatus === 'rejected' ? 'bg-red-100' : 'bg-gray-100'
              }`}>
                3
              </div>
              <span className="text-xs font-medium">Result</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Features */}
      {showSecurityFeatures && (
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Security Features:</h3>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Client-side SHA-256 hashing for integrity verification</li>
            <li>• Server-side hash validation to detect tampering</li>
            <li>• Automatic virus and malware scanning</li>
            <li>• Maximum file size: {maxFileSize}MB</li>
          </ul>
        </div>
      )}
    </div>
  );
};

// Example usage component
const ExampleUsage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Secure File Upload Center</h1>
        
        <SecureFileUpload 
          uploadEndpoint="http://88.198.60.90:8008/upload"
          maxFileSize={100}
          onUploadSuccess={(data) => {
            console.log('Upload successful:', data);
          }}
          onUploadError={(error) => {
            console.error('Upload failed:', error);
          }}
          showSecurityFeatures={true}
        />
      </div>
    </div>
  );
};

export default SecureFileUpload;